import React, { useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import styles from './Modal.module.css';

function getScrollbarWidth() {
	// Create an invisible container
	const outer = document.createElement('div');
	outer.style.visibility = 'hidden';
	outer.style.overflow = 'scroll'; // Forcing scrollbar to appear
	outer.style.msOverflowStyle = 'scrollbar'; // Needed for WinJS apps
	document.body.appendChild(outer);

	// Create an inner div without any scrollbars
	const inner = document.createElement('div');
	outer.appendChild(inner);

	// Calculate the scrollbar width
	const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

	// Remove the divs
	document.body.removeChild(outer);

	return scrollbarWidth;
}

const Modal = ({
	children,
	onClose,
	title,
	subtitle,
	headerRight,
	maxWidth = 720,
	showCloseButton = true,
	overlayClassName = '',
	contentClassName = '',
	headerClassName = '',
	bodyClassName = '',
}) => {
	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	useEffect(() => {
		const body = document.body;
		const openCount = Number(body.dataset.modalOpenCount || '0');
		const scrollbarWidth = getScrollbarWidth();

		if (openCount === 0) {
			const computedBodyStyles = window.getComputedStyle(body);
			const currentPaddingRight = parseFloat(computedBodyStyles.paddingRight) || 0;

			body.dataset.modalOriginalOverflow = body.style.overflow || '';
			body.dataset.modalOriginalPaddingRight = body.style.paddingRight || '';
			body.style.overflow = 'hidden';
			body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
		}

		body.dataset.modalOpenCount = String(openCount + 1);
		body.dataset.modalOpen = 'true';

		return () => {
			const nextOpenCount = Math.max(0, Number(body.dataset.modalOpenCount || '1') - 1);

			if (nextOpenCount === 0) {
				body.style.overflow = body.dataset.modalOriginalOverflow || '';
				body.style.paddingRight = body.dataset.modalOriginalPaddingRight || '';
				delete body.dataset.modalOpen;
				delete body.dataset.modalOpenCount;
				delete body.dataset.modalOriginalOverflow;
				delete body.dataset.modalOriginalPaddingRight;
				return;
			}

			body.dataset.modalOpenCount = String(nextOpenCount);
		};
	}, []);

	useEffect(() => {
		const handleEscape = (event) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};
		window.addEventListener('keydown', handleEscape);
		return () => window.removeEventListener('keydown', handleEscape);
	}, [onClose]);

	return (
		<div className={`${styles.ModalOverlay} ${overlayClassName}`.trim()} onClick={handleOverlayClick}>
			<div
				className={`${styles.ModalContent} ${contentClassName}`.trim()}
				style={{ maxWidth: `${maxWidth}px` }}
				role='dialog'
				aria-modal='true'
				aria-label={title || 'Modal'}
			>
				<div className={`${styles.ModalHeader} ${headerClassName}`.trim()}>
					<div className={styles.ModalHeadingBlock}>
						{title ? <h3 className={styles.ModalTitle}>{title}</h3> : null}
						{subtitle ? <p className={styles.ModalSubtitle}>{subtitle}</p> : null}
					</div>
					<div className={styles.ModalHeaderActions}>
						{headerRight}
						{showCloseButton ? (
							<button type='button' className={styles.CloseButton} onClick={onClose} aria-label='Close modal'>
								<MdClose />
							</button>
						) : null}
					</div>
				</div>
				<div className={`${styles.ModalBody} ${bodyClassName}`}>
					{children}
				</div>
			</div>
		</div>
	);
};

export default Modal;